<template>
  <div class="pt-32 pb-40 px-4 max-w-7xl mx-auto">
    <div v-if="pending" class="flex justify-center py-20">
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-main-color"
      />
    </div>
    <div v-else-if="error || !menu" class="text-center py-20">
      <h2 class="text-2xl font-bold text-red-500">
        Erreur lors du chargement du menu
      </h2>
      <UButton to="/menus" color="main" variant="ghost" class="mt-4">
        Retour aux menus
      </UButton>
    </div>
    <div v-else>
      <div class="mb-6">
        <UButton
          to="/menus"
          icon="i-lucide-arrow-left"
          variant="ghost"
          color="gray"
        >
          Retour aux menus
        </UButton>
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <!-- Left: Image -->
        <div
          class="rounded-xl overflow-hidden shadow-xl h-fit lg:sticky lg:top-32"
        >
          <img
            :src="menu.image"
            :alt="menu.name"
            class="w-full h-auto object-cover"
          />
        </div>

        <!-- Right: Details & Customization -->
        <div class="space-y-8">
          <div>
            <h1 class="text-4xl font-bold text-main-color mb-2">
              {{ menu.name }}
            </h1>
            <p class="text-2xl font-bold text-gray-700 dark:text-gray-300">
              {{ menu.price }}€
            </p>
          </div>

          <!-- Meals Section -->
          <div>
            <h3 class="text-xl font-semibold mb-4 border-b pb-2">Votre Menu</h3>
            <div class="space-y-4">
              <div
                v-for="content in menu.menu_content"
                :key="content.id"
                class="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm"
              >
                <div class="flex items-start gap-4">
                  <NuxtImg
                    :src="content.repas.image"
                    :alt="content.repas.name"
                    class="w-24 h-24 rounded-lg object-cover"
                  />
                  <div class="flex-1">
                    <h4 class="font-bold text-lg mb-1">
                      {{ content.repas.name }}
                    </h4>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      {{ getCustomizationSummary(content.id) }}
                    </p>
                    <UButton
                      size="sm"
                      color="main"
                      variant="soft"
                      icon="i-lucide-pencil"
                      @click="openMealCustomize(content)"
                    >
                      Personnaliser
                    </UButton>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sauces Selection -->
          <div v-if="availableSauces.length > 0">
            <h3
              class="text-xl font-semibold mb-4 border-b pb-2 flex justify-between items-center"
            >
              <span>Sauces</span>
              <span
                :class="{
                  'text-green-600': totalSaucesSelected <= menu.sauce_number,
                  'text-orange-500': totalSaucesSelected > menu.sauce_number,
                }"
                class="text-base"
              >
                {{ totalSaucesSelected }} / {{ menu.sauce_number }}
                <span
                  v-if="totalSaucesSelected > menu.sauce_number"
                  class="text-xs ml-1 font-normal"
                  >(Suppléments)</span
                >
              </span>
            </h3>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div
                v-for="sauce in availableSauces"
                :key="sauce.id"
                class="flex flex-col items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <NuxtImg
                  :src="sauce.image"
                  class="w-16 h-16 rounded-full object-cover"
                />
                <p class="text-base font-bold text-center line-clamp-1">
                  {{ sauce.name }}
                </p>
                <p class="text-sm text-main-color font-bold">
                  +{{ sauce.price }}€
                </p>
                <div
                  class="flex items-center gap-2 mt-auto bg-gray-100 dark:bg-gray-900 rounded-full px-2 py-1"
                >
                  <UButton
                    size="xs"
                    icon="i-lucide-minus"
                    variant="ghost"
                    color="gray"
                    :disabled="!selectedSauces[sauce.id]"
                    @click="decreaseSauce(sauce.id)"
                  />
                  <span class="text-sm font-medium w-4 text-center">{{
                    selectedSauces[sauce.id] || 0
                  }}</span>
                  <UButton
                    size="xs"
                    icon="i-lucide-plus"
                    variant="ghost"
                    color="gray"
                    @click="increaseSauce(sauce.id)"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Desserts Selection -->
          <div v-if="availableDesserts.length > 0">
            <h3
              class="text-xl font-semibold mb-4 border-b pb-2 flex justify-between items-center"
            >
              <span>Desserts</span>
              <span
                :class="{
                  'text-green-600':
                    totalDessertsSelected <= menu.dessert_number,
                  'text-orange-500':
                    totalDessertsSelected > menu.dessert_number,
                }"
                class="text-base"
              >
                {{ totalDessertsSelected }} / {{ menu.dessert_number }}
                <span
                  v-if="totalDessertsSelected > menu.dessert_number"
                  class="text-xs ml-1 font-normal"
                  >(Suppléments)</span
                >
              </span>
            </h3>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div
                v-for="dessert in availableDesserts"
                :key="dessert.id"
                class="flex flex-col items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <NuxtImg
                  :src="dessert.image"
                  class="w-16 h-16 rounded-full object-cover"
                />
                <p class="text-base font-bold text-center line-clamp-1">
                  {{ dessert.name }}
                </p>
                <p class="text-sm text-main-color font-bold">
                  +{{ dessert.price }}€
                </p>
                <div
                  class="flex items-center gap-2 mt-auto bg-gray-100 dark:bg-gray-900 rounded-full px-2 py-1"
                >
                  <UButton
                    size="xs"
                    icon="i-lucide-minus"
                    variant="ghost"
                    color="gray"
                    :disabled="!selectedDesserts[dessert.id]"
                    @click="decreaseDessert(dessert.id)"
                  />
                  <span class="text-sm font-medium w-4 text-center">{{
                    selectedDesserts[dessert.id] || 0
                  }}</span>
                  <UButton
                    size="xs"
                    icon="i-lucide-plus"
                    variant="ghost"
                    color="gray"
                    @click="increaseDessert(dessert.id)"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Total & CTA -->
          <div
            class="sticky bottom-4 bg-white dark:bg-gray-900 p-4 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-10"
          >
            <div class="flex justify-between items-center mb-4">
              <span class="text-gray-500">Total</span>
              <span class="text-3xl font-bold text-main-color"
                >{{ calculateTotalPrice().toFixed(2) }}€</span
              >
            </div>
            <UButton block size="xl" color="main" @click="addMenuToCart">
              Ajouter au panier
            </UButton>
          </div>
        </div>
      </div>
    </div>

    <!-- Meal Customization Drawer -->
    <MealCustomizeDrawer
      v-if="currentMealContent"
      v-model="isMealDrawerOpen"
      :meal="currentMealContent.repas"
      :existing-extras="customizations[currentMealContent.id]"
      :menu-context="{
        menuId: menu?.id || 0,
        menuName: menu?.name || '',
        isMenuMode: true,
      }"
      :auto-add-to-cart="false"
      @meal-customized="onMealCustomized"
    />
  </div>
</template>

<script setup lang="ts">
import type { Menu, MenuContent } from "~/models/menu";
import type { Repas, Ingredient } from "~/models/repas";
import type { Sauce } from "~/models/sauce";
import type { Dessert } from "~/models/dessert";

const route = useRoute();
const router = useRouter();
const { $api } = useNuxtApp();
const cartStore = useCartStore();
const { successToast, errorToast } = useToastMessages();

// Fetch Menu Data
const {
  data: menu,
  pending,
  error,
} = await useAsyncData<Menu>(`menu-${route.params.id}`, async () => {
  const res = await $api.get(`/menus/${route.params.id}`, "include");
  if (!res) throw new Error("Menu not found");

  // Map existing structure logic if needed (images remapping)
  // Assuming API returns similar structure to list but single object
  return {
    ...res,
    image: getImageUrl(res.image),
    menu_content: res.menu_content.map((mc: { repas: Repas }) => ({
      ...mc,
      repas: {
        ...mc.repas,
        image: getImageUrl(mc.repas.image),
        // Explicitly allow potential content/ingredients passthrough
        content: mc.repas.content || [],
      },
    })),
  };
});

// State
const customizations = ref<
  Record<number, Array<{ ingredient: Ingredient; quantity: number }>>
>({});
const customizedMealsData = ref<Record<number, Repas>>({}); // Store hydrated/updated meal objects for pricing
const mealExtraCosts = ref<Record<number, number>>({}); // Store pre-calculated extra costs

const availableSauces = ref<Sauce[]>([]);
const availableDesserts = ref<Dessert[]>([]);
const selectedSauces = ref<Record<number, number>>({});
const selectedDesserts = ref<Record<number, number>>({});

const isMealDrawerOpen = ref(false);
const currentMealContent = ref<MenuContent | null>(null);

// Fetch Extras
const fetchExtras = async () => {
  try {
    const [saucesRes, dessertsRes] = await Promise.all([
      $api.get("/sauce"),
      $api.get("/dessert"),
    ]);

    if (saucesRes) {
      availableSauces.value = saucesRes.map((s: Sauce) => ({
        ...s,
        image: getImageUrl(s.image),
      }));
    }
    if (dessertsRes) {
      availableDesserts.value = dessertsRes.map((d: Dessert) => ({
        ...d,
        image: getImageUrl(d.image),
      }));
    }
  } catch (e) {
    console.error("Failed to fetch extras", e);
  }
};

onMounted(() => {
  fetchExtras();
});

// Interactive Logic
const openMealCustomize = (content: MenuContent) => {
  currentMealContent.value = content;
  isMealDrawerOpen.value = true;
};

const onMealCustomized = (data: {
  meal: Repas;
  extras: Array<{ ingredient: Ingredient; quantity: number }>;
  extraCost: number;
}) => {
  if (currentMealContent.value) {
    customizations.value[currentMealContent.value.id] = data.extras;
    customizedMealsData.value[currentMealContent.value.id] = data.meal; // Store hydrated meal
    mealExtraCosts.value[currentMealContent.value.id] = data.extraCost; // Store cost
  }
};

const getCustomizationSummary = (contentId: number) => {
  const extras = customizations.value[contentId];
  if (!extras || extras.length === 0) return "Aucune modification";

  const storedMeal =
    customizedMealsData.value[contentId] ||
    menu.value?.menu_content.find((c) => c.id === contentId)?.repas;

  const originalIngredients: Record<string, number> = {};
  if (storedMeal) {
    if (storedMeal.content && storedMeal.content.length > 0) {
      storedMeal.content.forEach(
        (i: { ingredient?: { name: string }; quantity: number }) => {
          if (i.ingredient)
            originalIngredients[i.ingredient.name.toLowerCase()] = i.quantity;
        }
      );
    } else if (storedMeal.ingredients) {
      const parts = storedMeal.ingredients.split(",").map((s) => s.trim());
      parts.forEach((p) => {
        const match = p.match(/^(\d+)\s+(.+)$/);
        if (match) {
          originalIngredients[match[2].trim().toLowerCase()] = parseInt(
            match[1]
          );
        } else if (p.length > 0) {
          originalIngredients[p.trim().toLowerCase()] = 1;
        }
      });
    }
  }

  const changes: string[] = [];
  extras.forEach((e) => {
    const orig = originalIngredients[e.ingredient.name.toLowerCase()] || 0;
    const diff = e.quantity - orig;
    if (diff > 0) {
      changes.push(`+${diff} ${e.ingredient.name}`);
    } else if (diff < 0 && e.quantity === 0) {
      changes.push(`Sans ${e.ingredient.name}`);
    }
  });

  return changes.length > 0 ? changes.join(", ") : "Aucune modification";
};

// Pricing Logic
const calculatePricesForExtras = (
  items: Record<number, number>,
  available: { id: number; price: number }[],
  limit: number
) => {
  const prices: number[] = [];
  Object.entries(items).forEach(([id, qty]) => {
    const item = available.find((i) => i.id === parseInt(id));
    if (item) {
      for (let i = 0; i < qty; i++) prices.push(item.price);
    }
  });
  // Sort Descending (Expensive first are free)
  prices.sort((a, b) => b - a);
  // Skip first 'limit' items (Included)
  const toPay = prices.slice(limit);
  // Sum
  return toPay.reduce((a, b) => a + b, 0);
};

const calculateTotalPrice = () => {
  if (!menu.value) return 0;

  let totalExtrasCost = 0;

  // Utilize the costs provided by MealCustomizeDrawer
  Object.values(mealExtraCosts.value).forEach((cost) => {
    totalExtrasCost += cost;
  });

  const saucesCost = calculatePricesForExtras(
    selectedSauces.value,
    availableSauces.value,
    menu.value.sauce_number
  );

  const dessertsCost = calculatePricesForExtras(
    selectedDesserts.value,
    availableDesserts.value,
    menu.value.dessert_number
  );

  return menu.value.price + totalExtrasCost + saucesCost + dessertsCost;
};

// Selection Helpers
const totalSaucesSelected = computed(() =>
  Object.values(selectedSauces.value).reduce((a, b) => a + b, 0)
);
const totalDessertsSelected = computed(() =>
  Object.values(selectedDesserts.value).reduce((a, b) => a + b, 0)
);

const increaseSauce = (id: number) => {
  selectedSauces.value[id] = (selectedSauces.value[id] || 0) + 1;
};
const decreaseSauce = (id: number) => {
  if (selectedSauces.value[id] > 0) {
    selectedSauces.value[id]--;
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    if (selectedSauces.value[id] === 0) delete selectedSauces.value[id];
  }
};
const increaseDessert = (id: number) => {
  selectedDesserts.value[id] = (selectedDesserts.value[id] || 0) + 1;
};
const decreaseDessert = (id: number) => {
  if (selectedDesserts.value[id] > 0) {
    selectedDesserts.value[id]--;
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    if (selectedDesserts.value[id] === 0) delete selectedDesserts.value[id];
  }
};

const addMenuToCart = async () => {
  if (!menu.value) {
    errorToast("Erreur", "Impossible d'ajouter le menu (données manquantes)");
    return;
  }

  let isSuccess = false;

  try {
    // Hydrate menu content with full meal details (for correct cart calculations)
    const hydratedMenu = {
      ...menu.value,
      menu_content: menu.value.menu_content.map((mc) => {
        const hydratedMeal = customizedMealsData.value[mc.id];
        // Only replace if we have a better version (hydrated)
        // Note: hydratedMeal comes from MealCustomizeDrawer which fetched full details
        if (hydratedMeal) {
          return {
            ...mc,
            repas: hydratedMeal,
          };
        }
        return mc;
      }),
    };

    // Prepare Data using Map logic
    const customizationArray = Object.entries(customizations.value).map(
      ([contentId, extras]) => ({
        menuContentId: parseInt(contentId),
        extras,
      })
    );

    const saucesArray = Object.entries(selectedSauces.value)
      .map(([id, qty]) => {
        const sauce = availableSauces.value.find((s) => s.id === parseInt(id));
        return sauce ? { sauce, quantity: qty } : null;
      })
      .filter(
        (item): item is { sauce: Sauce; quantity: number } => item !== null
      );

    const dessertsArray = Object.entries(selectedDesserts.value)
      .map(([id, qty]) => {
        const dessert = availableDesserts.value.find(
          (d) => d.id === parseInt(id)
        );
        return dessert ? { dessert, quantity: qty } : null;
      })
      .filter(
        (item): item is { dessert: Dessert; quantity: number } => item !== null
      );

    // Pass hydratedMenu instead of raw menu.value
    cartStore.addMenu(
      hydratedMenu,
      customizationArray,
      saucesArray,
      dessertsArray
    );

    isSuccess = true;
    successToast("Menu ajouté", "Le menu a été ajouté au panier");

    await router.push("/menus").catch((err) => {
      console.warn("Navigation error ignored:", err);
    });
  } catch (e) {
    if (isSuccess) {
      console.warn(
        "Suppressing error toast because cart add was successful",
        e
      );
      return;
    }
    console.error("Error adding menu to cart", e);
    errorToast("Erreur", "Une erreur est survenue lors de l'ajout au panier");
  }
};
</script>
